/**
 * A function that yields control back to the browser's main thread to allow it
 * to perform other tasks, such as rendering or processing events.
 *
 * If the browser supports the `scheduler.yield` API, it uses it to yield
 * control. Otherwise, it falls back to using a zero-millisecond timeout
 * to achieve a similar effect.
 *
 * This function is useful in scenarios where you have long-running
 * JavaScript operations and want to prevent UI blocking or improve
 * the responsiveness of web applications by yielding control periodically.
 *
 * @return {Promise<void>} A Promise that resolves when the yielding process is complete.
 */
export function yieldToMain() {
	if ( 'scheduler' in window && 'yield' in window.scheduler ) {
		return window.scheduler.yield();
	}
	return new Promise( ( resolve ) => setTimeout( resolve, 0 ) );
}

/**
 * Creates and returns a promise with exposed `resolve` and `reject` methods.
 * If the environment supports `Promise.withResolvers`, it utilizes that implementation.
 * Otherwise, it creates a custom implementation by attaching `resolve` and `reject`
 * methods directly to the promise instance.
 *
 * @return {PromiseWithResolvers} A promise object with additional `resolve` and `reject` methods.
 */
export function PromiseWithResolvers() {
	if ( 'withResolvers' in Promise ) {
		return Promise.withResolvers();
	}

	let resolve, reject;
	const promise = new Promise( ( res, rej ) => {
		resolve = res;
		reject = rej;
	} );
	return { promise, resolve, reject };
}
